// =============================================================================
// App shell — collapsible sidebar + persona switcher + content area.
// =============================================================================
import { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import {
  LayoutGrid, Table2, Radar, BarChart3, FileText, History,
  MessageCircle, PanelLeftClose, PanelLeftOpen, ChevronDown,
} from 'lucide-react'
import { usePersonaSlice, setPersona } from '../store/usePageSlices'
import type { Persona } from '../lib/types'

const NAV = [
  { to: '/', icon: Table2, label: 'Sites' },
  { to: '/dashboard', icon: BarChart3, label: 'Dashboard' },
  { to: '/intelligence', icon: Radar, label: 'Intelligence' },
  { to: '/activity', icon: History, label: 'Activity' },
  { to: '/report', icon: FileText, label: 'Report' },
  { to: '/chat', icon: MessageCircle, label: 'AI Portal' },
]

export function Layout({ personas }: { personas: Persona[] }) {
  const [collapsed, setCollapsed] = useState(false)
  // The active persona ("user view") lives in the global store: it scopes the Sites list
  // and tags edits. The roster is server-owned (meta.personas, single-sourced from the
  // site_owner_pm enum) and passed in. No auth in this PoC -- the selection IS the identity.
  const persona = usePersonaSlice((s) => s.persona)
  const [showPersonas, setShowPersonas] = useState(false)

  return (
    <div className="flex h-screen">
      {/* Sidebar */}
      <nav className={`no-print flex flex-col border-r border-line bg-white transition-all duration-200 ${collapsed ? 'w-14' : 'w-56'}`}>
        {/* Logo / header */}
        <div className="flex items-center gap-2 px-3 py-4">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-brand-600">
            <LayoutGrid className="h-4 w-4 text-white" />
          </div>
          {!collapsed && <span className="sidebar-header-text text-sm font-bold tracking-tight text-ink">Site Delivery Tool: RMG</span>}
        </div>

        {/* Nav items */}
        <div className="flex-1 space-y-0.5 px-2">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              title={n.label}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-50 text-brand-700 border-l-3 border-brand-600'
                    : 'text-muted hover:bg-canvas hover:text-ink'
                }`
              }
            >
              <n.icon className="h-4 w-4 flex-shrink-0" />
              {!collapsed && <span className="sidebar-label">{n.label}</span>}
            </NavLink>
          ))}
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="mx-2 mb-2 flex items-center gap-2 rounded-md px-3 py-2 text-[12px] text-muted hover:bg-canvas hover:text-ink"
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <><PanelLeftClose className="h-4 w-4" /><span className="sidebar-label">Collapse</span></>}
        </button>

        {/* Footer */}
        {!collapsed && (
          <div className="border-t border-line px-3 py-3">
            <p className="text-[10px] text-muted">RMG Site Delivery Tool v1.0</p>
          </div>
        )}
      </nav>

      {/* Main content */}
      <main className="flex-1 overflow-hidden flex flex-col">
        {/* Top bar with persona switcher */}
        <header className="no-print flex items-center justify-between border-b border-line bg-white px-5 py-2.5">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-bt-green" />
            <span className="text-[12px] font-medium text-ink">{persona.name}</span>
            <span className="text-[11px] text-muted">{persona.role}</span>
          </div>

          {/* Persona switcher dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowPersonas(!showPersonas)}
              className="flex items-center gap-2 rounded-md border border-line px-3 py-1.5 text-[12px] font-medium text-ink hover:bg-canvas"
            >
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-700">
                {persona.name.split(' ').map((w) => w[0]).join('').slice(0, 2)}
              </div>
              {persona.name}
              <ChevronDown className="h-3 w-3 text-muted" />
            </button>
            {showPersonas && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowPersonas(false)} />
                <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-md border border-line bg-white p-1 shadow-lift animate-slide-down">
                  {personas.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setPersona(p); setShowPersonas(false) }}
                      className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-[12px] hover:bg-canvas ${persona.id === p.id ? 'bg-brand-50 text-brand-700' : 'text-ink'}`}
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-100 text-[9px] font-bold text-brand-700">
                        {p.name.split(' ').map((w) => w[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <p className="font-medium">{p.name}</p>
                        <p className="text-[10px] text-muted">{p.role}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-auto px-5 py-4">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
